import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { DoxMascot } from "@/components/Logo";

/**
 * DoxAmbient — Dox is never idle.
 *
 * Wraps the mascot with subtle, purposeful life:
 *   • Breathing (slow y-scale)
 *   • Occasional confident nod (rare, quiet)
 *   • Occasional glance-back-to-paperwork gesture
 *   • A whisper-quiet "still working" beat: a small paper icon nearby
 *     drifts across the desk every 15-25s, then a subtle stamp mark
 *
 * Every movement answers ONE question — "What is Dox doing right now?" —
 * and never distracts from the customer's peace of mind.
 *
 * Respects prefers-reduced-motion. Never runs faster than 1 gesture / ~10s.
 */
export default function DoxAmbient({ size = 130, testid = "dox-ambient" }) {
  const reduce = useReducedMotion();
  const [gesture, setGesture] = useState("still"); // "still" | "glance" | "nod"

  useEffect(() => {
    if (reduce) return;
    let alive = true;
    const pickNext = () => {
      const wait = 9000 + Math.random() * 8000; // 9–17s between gestures
      setTimeout(() => {
        if (!alive) return;
        // 70% glance (looks back at paperwork), 30% nod (small confident nod)
        setGesture(Math.random() < 0.7 ? "glance" : "nod");
        setTimeout(() => {
          if (!alive) return;
          setGesture("still");
          pickNext();
        }, gesture === "nod" ? 900 : 1200);
      }, wait);
    };
    pickNext();
    return () => { alive = false; };
  }, [reduce]);

  // Gesture → animation targets. Everything is intentionally small.
  const gestureAnim = reduce
    ? { y: 0, rotate: 0 }
    : gesture === "nod"
    ? { y: [0, 3.5, 0], rotate: [0, 1.6, 0] }
    : gesture === "glance"
    ? { y: [0, 1, 0], rotate: [0, -1.4, 0.6, 0] }
    : { y: 0, rotate: 0 };

  return (
    <div
      className="relative inline-flex items-center justify-center"
      data-testid={testid}
      aria-label="Dox — working quietly"
    >
      {/* Ambient desk trace — a faint page silhouette that drifts every ~20s */}
      {!reduce && <DriftingPage />}

      {/* Slow breathing — a permanent, near-imperceptible scale-y oscillation */}
      <motion.div
        animate={reduce ? {} : { scale: [1, 1.014, 1] }}
        transition={{ duration: 4.6, ease: "easeInOut", repeat: Infinity }}
      >
        {/* Rare purposeful gesture layered on top */}
        <motion.div
          animate={gestureAnim}
          transition={{
            duration: gesture === "nod" ? 0.9 : 1.2,
            ease: [0.33, 1, 0.68, 1],
          }}
        >
          <DoxMascot size={size} emotion={gesture === "glance" ? "reading" : "idle"} />
        </motion.div>
      </motion.div>
    </div>
  );
}

/**
 * A whisper-quiet paper icon that drifts behind Dox every ~20s.
 * Communicates: "Dox is still working."
 */
function DriftingPage() {
  return (
    <motion.div
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-6 -right-8 opacity-25"
      initial={{ x: -60, opacity: 0 }}
      animate={{
        x: [-60, -14, 30],
        opacity: [0, 0.35, 0],
      }}
      transition={{
        duration: 5.2,
        ease: [0.33, 1, 0.68, 1],
        repeat: Infinity,
        repeatDelay: 15 + Math.random() * 6,
      }}
    >
      <svg width="34" height="42" viewBox="0 0 34 42" fill="none">
        <rect x="1" y="1" width="32" height="40" rx="3" fill="#FFFFFF" stroke="#0B1B4D" strokeWidth="1.2" opacity="0.85" />
        <line x1="7" y1="10" x2="27" y2="10" stroke="#1E78FF" strokeWidth="1.4" strokeLinecap="round" />
        <line x1="7" y1="16" x2="24" y2="16" stroke="#0B1B4D" strokeOpacity="0.5" strokeWidth="1" strokeLinecap="round" />
        <line x1="7" y1="21" x2="21" y2="21" stroke="#0B1B4D" strokeOpacity="0.4" strokeWidth="1" strokeLinecap="round" />
        <line x1="7" y1="26" x2="18" y2="26" stroke="#0B1B4D" strokeOpacity="0.3" strokeWidth="1" strokeLinecap="round" />
      </svg>
    </motion.div>
  );
}
